import { useState } from "react";

const ContactModal = ({ onClose }: { onClose: () => void }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl w-full max-w-lg z-10 overflow-hidden">
        {/* Header */}
        <div className="px-8 pt-8 pb-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-green-700 text-xs font-semibold tracking-widest mb-1">Contact</p>
            <h2 className="text-gray-800 text-xl font-bold">お問い合わせ</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            aria-label="閉じる"
          >
            <i className="ri-close-line text-gray-600 text-lg" />
          </button>
        </div>

        <div className="px-8 py-8 flex flex-col gap-5">
          {/* Phone */}
          <div className="bg-green-50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-600 text-white shrink-0">
                <i className="ri-phone-line text-lg" />
              </div>
              <div>
                <p className="text-gray-500 text-xs font-semibold">お電話でのお問い合わせ</p>
                <p className="text-gray-700 text-xs mt-0.5">受付時間 9:00〜17:30（平日）</p>
              </div>
            </div>
            <a
              href="tel:0120-000-000"
              className="block text-green-700 text-3xl font-bold tracking-wider hover:text-green-800 transition-colors"
            >
              0120-000-000
            </a>
          </div>

          {/* SNS */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-700 text-white shrink-0">
                <i className="ri-share-line text-lg" />
              </div>
              <p className="text-gray-700 text-sm font-semibold">SNSでのご連絡</p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href="#"
                className="flex items-center justify-between px-4 py-3 bg-white rounded-xl hover:bg-green-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "#06C755" }}>
                    <i className="ri-line-fill text-white text-base" />
                  </div>
                  <span className="text-gray-700 text-sm font-medium">LINE公式アカウント</span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-external-link-line text-gray-400 text-sm group-hover:text-green-600 transition-colors" />
                </div>
              </a>
              <a
                href="#"
                className="flex items-center justify-between px-4 py-3 bg-white rounded-xl hover:bg-green-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }}>
                    <i className="ri-instagram-line text-white text-base" />
                  </div>
                  <span className="text-gray-700 text-sm font-medium">Instagram</span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-external-link-line text-gray-400 text-sm group-hover:text-green-600 transition-colors" />
                </div>
              </a>
              <a
                href="#"
                className="flex items-center justify-between px-4 py-3 bg-white rounded-xl hover:bg-green-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "#1877F2" }}>
                    <i className="ri-facebook-circle-fill text-white text-base" />
                  </div>
                  <span className="text-gray-700 text-sm font-medium">Facebook</span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-external-link-line text-gray-400 text-sm group-hover:text-green-600 transition-colors" />
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactSection = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    document.body.style.overflow = "hidden";
    setModalOpen(true);
  };

  const closeModal = () => {
    document.body.style.overflow = "";
    setModalOpen(false);
  };

  return (
    <>
      <section id="contact" className="py-20 lg:py-32 px-6 bg-[#f5f0e8]">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center gap-12">
            <div className="lg:w-80 shrink-0">
              <p className="text-green-700 text-xs font-semibold tracking-widest mb-2">Contact</p>
              <h2 className="text-gray-800 text-2xl lg:text-3xl font-bold">お問い合わせ</h2>
              <p className="mt-6 text-gray-600 text-sm lg:text-base leading-relaxed">
                施設見学や各サービスについての質問などお気軽にご連絡ください。みどり苑はいつでもあなたのそばにいます。
              </p>
            </div>

            <div className="flex-1">
              <button
                type="button"
                onClick={openModal}
                className="w-full relative bg-white rounded-2xl py-12 lg:py-16 px-8 lg:px-14 flex flex-col gap-2 hover:bg-green-50 transition-colors cursor-pointer group overflow-hidden text-left"
              >
                {/* Icon row */}
                <div className="flex gap-3 mb-2">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100 text-green-600">
                    <i className="ri-phone-line text-base" />
                  </div>
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100 text-green-600">
                    <i className="ri-line-fill text-base" />
                  </div>
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100 text-green-600">
                    <i className="ri-instagram-line text-base" />
                  </div>
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100 text-green-600">
                    <i className="ri-facebook-circle-line text-base" />
                  </div>
                </div>
                <span className="text-gray-500 text-xs font-bold tracking-widest">Contact</span>
                <span className="text-gray-800 text-xl lg:text-2xl font-bold">電話・SNSでお問い合わせ</span>
                <p className="text-gray-500 text-sm">タップして連絡先を確認する</p>
                <div className="absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-green-600 text-white group-hover:bg-green-700 transition-colors">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {modalOpen && <ContactModal onClose={closeModal} />}
    </>
  );
};

export default ContactSection;
